import * as React from "react";

const TAB_TTL_MS = 4000;
const HEARTBEAT_MS = 1500;

function getChannelName(roomKey) {
  return `live-interview-tabs:${String(roomKey || "default")}`;
}

function readTabRegistry(roomKey) {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = window.sessionStorage.getItem(getChannelName(roomKey));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeTabRegistry(roomKey, registry) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(getChannelName(roomKey), JSON.stringify(registry));
  } catch {
    // Ignore quota / private mode errors.
  }
}

function countActiveTabs(roomKey) {
  const now = Date.now();
  const registry = readTabRegistry(roomKey);
  return Object.values(registry).filter((seenAt) => now - Number(seenAt) < TAB_TTL_MS).length;
}

export function useLiveInterviewTabPresence(roomKey) {
  const tabIdRef = React.useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const [tabCount, setTabCount] = React.useState(1);

  React.useEffect(() => {
    if (!roomKey || typeof window === "undefined") {
      return undefined;
    }

    const tabId = tabIdRef.current;
    const channelName = getChannelName(roomKey);
    const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(channelName) : null;

    const heartbeat = () => {
      const registry = readTabRegistry(roomKey);
      registry[tabId] = Date.now();
      Object.keys(registry).forEach((key) => {
        if (Date.now() - Number(registry[key]) > TAB_TTL_MS) {
          delete registry[key];
        }
      });
      writeTabRegistry(roomKey, registry);
      const nextCount = countActiveTabs(roomKey);
      setTabCount(nextCount);
      channel?.postMessage({ type: "heartbeat", tabId, count: nextCount });
    };

    heartbeat();
    const intervalId = window.setInterval(heartbeat, HEARTBEAT_MS);
    channel?.addEventListener("message", heartbeat);

    return () => {
      window.clearInterval(intervalId);
      channel?.removeEventListener("message", heartbeat);
      channel?.close();
      const registry = readTabRegistry(roomKey);
      delete registry[tabId];
      writeTabRegistry(roomKey, registry);
    };
  }, [roomKey]);

  return {
    tabCount,
    isMultiTabInRoom: tabCount >= 2,
  };
}

export function useScreenShareDisplaySurface(trackRef) {
  const [displaySurface, setDisplaySurface] = React.useState("");

  React.useEffect(() => {
    if (!trackRef?.publication) {
      setDisplaySurface("");
      return undefined;
    }

    const readSurface = () => {
      const mediaTrack =
        trackRef.publication?.videoTrack?.mediaStreamTrack ??
        trackRef.publication?.track?.mediaStreamTrack;
      const surface = String(mediaTrack?.getSettings?.().displaySurface || "").trim();
      setDisplaySurface(surface);
    };

    readSurface();
    const mediaTrack =
      trackRef.publication?.videoTrack?.mediaStreamTrack ??
      trackRef.publication?.track?.mediaStreamTrack;
    mediaTrack?.addEventListener?.("started", readSurface);
    const intervalId = window.setInterval(readSurface, 1000);

    return () => {
      mediaTrack?.removeEventListener?.("started", readSurface);
      window.clearInterval(intervalId);
    };
  }, [
    trackRef?.publication?.trackSid,
    trackRef?.publication?.videoTrack,
    trackRef?.publication?.track,
  ]);

  return displaySurface;
}
